import { useCallback, useEffect, useRef, useState } from "react";
import { Sparkles, Loader2, ArrowRight, RefreshCw, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../../lib/api";
import { errText } from "../../auth/EmailCode";
import CvDownloadPanel from "../CvDownloadPanel";
import { Section } from "./ProfileFields";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const AUTO_DELAY = 4000;

export const canBuild = (b) => Boolean(b.name?.trim()) && (Boolean(b.summary?.trim()) || (b.experience || []).some((j) => j.role?.trim() || j.company?.trim()));

export default function ProfileCv({ body, baseCv, version, onBuilt, onGo }) {
  const [busy, setBusy] = useState(false);
  const alive = useRef(true);
  const running = useRef(false);
  const again = useRef(false);
  const latest = useRef({ body, onBuilt });
  latest.current = { body, onBuilt };
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const fromProfile = baseCv?.source === "profile" && baseCv?.structured;
  const auto = !baseCv || baseCv.source === "profile";

  const build = useCallback(async (manual = false) => {
    if (running.current) { again.current = true; return; }
    const data0 = latest.current.body();
    if (!canBuild(data0)) { if (manual) toast.error("Add your name and at least one job or a few lines about you first."); return; }
    running.current = true;
    setBusy(true);
    try {
      const { data } = await api.post("/profile/build-cv", data0);
      const started = Date.now();
      while (alive.current && Date.now() - started < 180000) {
        await sleep(2500);
        const { data: job } = await api.get(`/cv/import/${data.id}`);
        if (job.status === "done") { await latest.current.onBuilt(); if (manual) toast.success("Your CV is up to date."); break; }
        if (job.status === "failed") throw new Error("failed");
      }
    } catch (e) {
      toast.error(errText(e, "We couldn't update your CV this time. We'll try again when you next make a change."));
    } finally {
      running.current = false;
      if (alive.current) setBusy(false);
      if (again.current && alive.current) { again.current = false; build(); }
    }
  }, []);

  useEffect(() => {
    if (!version || !auto) return;
    const t = setTimeout(() => build(), AUTO_DELAY);
    return () => clearTimeout(t);
  }, [version, auto]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Section title="Your CV from this profile" testid="profile-cv"
      hint={auto ? "Your CV rewrites itself a few seconds after you stop typing, packed with the keywords recruiters search for." : "You're using an uploaded CV. Switch to your profile to have your CV update automatically."}>
      <div className="flex flex-wrap items-center gap-3">
        {busy ? (
          <span className="inline-flex items-center gap-2 text-sm font-medium text-brand-text" data-testid="profile-build-progress"><Loader2 className="w-4 h-4 animate-spin" /> Updating your CV…</span>
        ) : fromProfile ? (
          <span className="inline-flex items-center gap-2 text-sm font-medium text-success" data-testid="profile-cv-uptodate"><CheckCircle2 className="w-4 h-4" /> Your CV is up to date</span>
        ) : null}
        <button data-testid="profile-build-cv-button" onClick={() => build(true)} disabled={busy} className={`gi-btn ${fromProfile ? "gi-btn-secondary gi-btn-sm" : "gi-btn-primary"}`}>
          {fromProfile ? <RefreshCw className="w-4 h-4" aria-hidden="true" /> : <Sparkles className="w-4 h-4" aria-hidden="true" />}
          {fromProfile ? "Update now" : baseCv ? "Use my profile as my CV" : "Build my CV now"}
        </button>
        {fromProfile && !busy && (
          <button data-testid="profile-tailor-button" onClick={() => onGo("studio")} className="gi-btn gi-btn-secondary gi-btn-sm">
            Tailor it to a job <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </button>
        )}
      </div>
      {baseCv && !fromProfile && !busy && (
        <p className="mt-3 text-sm text-body" data-testid="profile-replace-note">
          This will replace <span className="font-medium text-ink">{baseCv.original_filename}</span> as the CV we use for your applications.
        </p>
      )}
      {fromProfile && <div className="mt-6"><CvDownloadPanel cv={baseCv.structured} testid="profile-cv-download" pathFor={(fmt, d) => `/cv/download.${fmt}?template=${d}`} /></div>}
    </Section>
  );
}
